import { useEffect, useState } from 'react';
import {
  Check,
  Copy,
  Download,
  ExternalLink,
  Globe,
  Laptop,
  Monitor,
  ShieldCheck,
  Smartphone,
  Sparkles,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  DESKTOP_PACKAGES,
  DESKTOP_VERSION,
  DetectedOs,
  DesktopPackage,
  GITHUB_RELEASES_URL,
  MacArch,
  MOBILE_VERSION,
  desktopPackageUrl,
  detectMacArch,
  detectOs,
  macPackage,
  mobileApkUrl,
  primaryPackageFor,
} from '@/components/marketing/download/v2/shared';
import { useAppDownloadStore } from '@/stores/appDownloadStore';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => void;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const getPackageByKey = (key: string): DesktopPackage =>
  DESKTOP_PACKAGES.find((p) => p.key === key) ?? (DESKTOP_PACKAGES[0] as DesktopPackage);

export function AppDownloadModal() {
  const { isOpen, closeModal } = useAppDownloadStore();
  const [activeTab, setActiveTab] = useState<'desktop' | 'mobile' | 'pwa'>('desktop');
  const [userOs, setUserOs] = useState<DetectedOs>('other');
  const [macArch, setMacArch] = useState<MacArch>('unknown');
  const [copiedLink, setCopiedLink] = useState(false);
  const [pwaPrompt, setPwaPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isPwaInstalled, setIsPwaInstalled] = useState(false);

  useEffect(() => {
    setUserOs(detectOs());
    detectMacArch().then(setMacArch);

    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setPwaPrompt(e as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      setIsPwaInstalled(true);
      setPwaPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsPwaInstalled(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Déterminer le paquet recommandé selon l'OS et l'architecture
  const getRecommendedPackage = (): DesktopPackage => {
    if (userOs === 'mac') {
      const arch = macArch === 'arm64' ? 'arm64' : 'x86_64';
      return macPackage(arch);
    }
    return primaryPackageFor(userOs);
  };

  const recommendedPkg = getRecommendedPackage();
  const recommendedUrl = desktopPackageUrl(recommendedPkg);
  const apkUrl = mobileApkUrl();
  const qrCodeApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=10&data=${encodeURIComponent(
    apkUrl,
  )}`;

  const handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch {
      // Fallback si clipboard async échoue
    }
  };

  const handleInstallPwa = async () => {
    if (!pwaPrompt) return;
    pwaPrompt.prompt();
    const { outcome } = await pwaPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsPwaInstalled(true);
    }
    setPwaPrompt(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent className="max-w-2xl border-slate-200 p-0 max-h-[85vh] sm:max-h-[90vh] flex flex-col overflow-hidden bg-white dark:border-charcoal-700 dark:bg-charcoal-900 rounded-2xl shadow-2xl">
        {/* En-tête héroïque style ChatGPT / Claude */}
        <div className="shrink-0 relative border-b border-slate-100 bg-gradient-to-br from-lime-500/10 via-slate-50 to-white px-5 sm:px-6 pt-5 sm:pt-6 pb-4 sm:pb-5 dark:border-charcoal-800 dark:from-lime-500/5 dark:via-charcoal-900 dark:to-charcoal-900">
          <DialogHeader className="gap-1.5 text-left">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-lime-500/15 px-2.5 py-0.5 font-mono text-[11px] font-semibold text-lime-700 dark:bg-lime-400/20 dark:text-lime-300">
                <Sparkles className="size-3" />
                Applications Velko
              </span>
              <span className="text-[11px] font-medium text-slate-400 dark:text-slate-500">
                v{DESKTOP_VERSION} Desktop · v{MOBILE_VERSION} Mobile
              </span>
            </div>
            <DialogTitle className="text-xl font-bold font-head tracking-tight text-charcoal-950 dark:text-slate-50">
              Télécharger Velko POS
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-600 dark:text-slate-400">
              Profitez d'une expérience native fluide, de la synchronisation instantanée et du mode
              hors-ligne pour votre caisse et votre gestion.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Section Recommandation Auto-détectée & Contenu scrollable */}
        <div className="overflow-y-auto flex-1 p-4 sm:p-6 space-y-5 sm:space-y-6">
          <div className="relative overflow-hidden rounded-xl border border-lime-500/30 bg-gradient-to-r from-lime-500/10 via-lime-500/5 to-transparent p-4 dark:border-lime-500/20 dark:from-lime-500/10">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="grid size-10 shrink-0 place-items-center rounded-lg bg-lime-500 text-charcoal-950 font-bold shadow-md">
                  <Laptop className="size-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-lime-700 dark:text-lime-400">
                      Recommandé pour votre appareil
                    </span>
                    <span className="inline-block size-1.5 rounded-full bg-lime-500 animate-ping" />
                  </div>
                  <h4 className="font-semibold text-sm text-charcoal-950 dark:text-slate-100">
                    Velko Desktop — {recommendedPkg.osLabel} ({recommendedPkg.arch})
                  </h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Format {recommendedPkg.format} · Version officielle certifiée
                  </p>
                </div>
              </div>

              <a
                href={recommendedUrl}
                download
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-lime-500 px-4 py-2.5 text-xs font-bold text-charcoal-950 transition-all hover:bg-lime-400 hover:shadow-lg hover:shadow-lime-500/20 focus:outline-none focus:ring-2 focus:ring-lime-500 active:scale-[0.98]"
              >
                <Download className="size-4" strokeWidth={2.25} />
                <span>Télécharger ({recommendedPkg.format})</span>
              </a>
            </div>
          </div>

          {/* Navigation par onglets (Desktop, Mobile, PWA) */}
          <div className="space-y-4">
            <div className="flex gap-1.5 rounded-xl bg-slate-100 p-1.5 dark:bg-charcoal-800 overflow-x-auto">
              <button
                type="button"
                onClick={() => setActiveTab('desktop')}
                className={`flex-1 min-w-[100px] flex items-center justify-center gap-1.5 sm:gap-2 rounded-lg py-2 px-2 sm:px-3 text-xs font-semibold transition-all ${
                  activeTab === 'desktop'
                    ? 'bg-white text-charcoal-950 shadow-xs dark:bg-charcoal-700 dark:text-slate-50'
                    : 'text-slate-600 hover:text-charcoal-950 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                <Monitor className="size-3.5 shrink-0" />
                <span className="truncate">
                  <span className="hidden sm:inline">Ordinateur (Desktop)</span>
                  <span className="sm:inline hidden">Desktop</span>
                  <span className="inline sm:hidden">Desktop</span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('mobile')}
                className={`flex-1 min-w-[100px] flex items-center justify-center gap-1.5 sm:gap-2 rounded-lg py-2 px-2 sm:px-3 text-xs font-semibold transition-all ${
                  activeTab === 'mobile'
                    ? 'bg-white text-charcoal-950 shadow-xs dark:bg-charcoal-700 dark:text-slate-50'
                    : 'text-slate-600 hover:text-charcoal-950 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                <Smartphone className="size-3.5 shrink-0" />
                <span className="truncate">
                  <span className="hidden sm:inline">Mobile (Android APK)</span>
                  <span className="inline sm:hidden">Mobile (APK)</span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('pwa')}
                className={`flex-1 min-w-[80px] flex items-center justify-center gap-1.5 sm:gap-2 rounded-lg py-2 px-2 sm:px-3 text-xs font-semibold transition-all ${
                  activeTab === 'pwa'
                    ? 'bg-white text-charcoal-950 shadow-xs dark:bg-charcoal-700 dark:text-slate-50'
                    : 'text-slate-600 hover:text-charcoal-950 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                <Globe className="size-3.5 shrink-0" />
                <span className="truncate">Web PWA</span>
              </button>
            </div>

            {/* Onglet Desktop */}
            {activeTab === 'desktop' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                {/* macOS */}
                <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-3.5 flex flex-col justify-between dark:border-charcoal-700 dark:bg-charcoal-800/40">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-xs text-charcoal-950 dark:text-slate-100">
                        macOS 11+
                      </span>
                      <span className="rounded bg-slate-200/70 px-1.5 py-0.5 text-[10px] font-mono text-slate-600 dark:bg-charcoal-700 dark:text-slate-300">
                        .dmg
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      Apple Silicon & Intel x86
                    </p>
                  </div>
                  <div className="mt-3 flex flex-col gap-1.5">
                    <a
                      href={desktopPackageUrl(macPackage('arm64'))}
                      download
                      className="inline-flex items-center justify-between rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700 border border-slate-200 hover:border-lime-500 hover:text-lime-600 dark:bg-charcoal-800 dark:text-slate-200 dark:border-charcoal-700 dark:hover:border-lime-500 dark:hover:text-lime-400 transition-colors"
                    >
                      <span>Apple Silicon (M1/M2/M3)</span>
                      <Download className="size-3" />
                    </a>
                    <a
                      href={desktopPackageUrl(macPackage('x86_64'))}
                      download
                      className="inline-flex items-center justify-between rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700 border border-slate-200 hover:border-lime-500 hover:text-lime-600 dark:bg-charcoal-800 dark:text-slate-200 dark:border-charcoal-700 dark:hover:border-lime-500 dark:hover:text-lime-400 transition-colors"
                    >
                      <span>Intel (x86_64)</span>
                      <Download className="size-3" />
                    </a>
                  </div>
                </div>

                {/* Windows */}
                <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-3.5 flex flex-col justify-between dark:border-charcoal-700 dark:bg-charcoal-800/40">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-xs text-charcoal-950 dark:text-slate-100">
                        Windows 10/11
                      </span>
                      <span className="rounded bg-slate-200/70 px-1.5 py-0.5 text-[10px] font-mono text-slate-600 dark:bg-charcoal-700 dark:text-slate-300">
                        .exe / .msi
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      Installer standard & entreprise
                    </p>
                  </div>
                  <div className="mt-3 flex flex-col gap-1.5">
                    <a
                      href={desktopPackageUrl(getPackageByKey('win-exe'))}
                      download
                      className="inline-flex items-center justify-between rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700 border border-slate-200 hover:border-lime-500 hover:text-lime-600 dark:bg-charcoal-800 dark:text-slate-200 dark:border-charcoal-700 dark:hover:border-lime-500 dark:hover:text-lime-400 transition-colors"
                    >
                      <span>Installer (.exe 64-bit)</span>
                      <Download className="size-3" />
                    </a>
                    <a
                      href={desktopPackageUrl(getPackageByKey('win-msi'))}
                      download
                      className="inline-flex items-center justify-between rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700 border border-slate-200 hover:border-lime-500 hover:text-lime-600 dark:bg-charcoal-800 dark:text-slate-200 dark:border-charcoal-700 dark:hover:border-lime-500 dark:hover:text-lime-400 transition-colors"
                    >
                      <span>MSI Package (.msi)</span>
                      <Download className="size-3" />
                    </a>
                  </div>
                </div>

                {/* Linux */}
                <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-3.5 flex flex-col justify-between dark:border-charcoal-700 dark:bg-charcoal-800/40">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-xs text-charcoal-950 dark:text-slate-100">
                        Linux
                      </span>
                      <span className="rounded bg-slate-200/70 px-1.5 py-0.5 text-[10px] font-mono text-slate-600 dark:bg-charcoal-700 dark:text-slate-300">
                        .deb / .AppImage
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      Ubuntu, Debian, Fedora & Portable
                    </p>
                  </div>
                  <div className="mt-3 flex flex-col gap-1.5">
                    <a
                      href={desktopPackageUrl(getPackageByKey('linux-deb'))}
                      download
                      className="inline-flex items-center justify-between rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700 border border-slate-200 hover:border-lime-500 hover:text-lime-600 dark:bg-charcoal-800 dark:text-slate-200 dark:border-charcoal-700 dark:hover:border-lime-500 dark:hover:text-lime-400 transition-colors"
                    >
                      <span>Debian / Ubuntu (.deb)</span>
                      <Download className="size-3" />
                    </a>
                    <a
                      href={desktopPackageUrl(getPackageByKey('linux-appimage'))}
                      download
                      className="inline-flex items-center justify-between rounded-lg bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700 border border-slate-200 hover:border-lime-500 hover:text-lime-600 dark:bg-charcoal-800 dark:text-slate-200 dark:border-charcoal-700 dark:hover:border-lime-500 dark:hover:text-lime-400 transition-colors"
                    >
                      <span>AppImage (Portable)</span>
                      <Download className="size-3" />
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Onglet Mobile */}
            {activeTab === 'mobile' && (
              <div className="flex flex-col sm:flex-row gap-4 items-center rounded-xl border border-slate-200 p-4 dark:border-charcoal-700 bg-slate-50/50 dark:bg-charcoal-800/40">
                <div className="grid size-36 shrink-0 place-items-center rounded-xl bg-white p-2 border border-slate-200 shadow-xs dark:bg-charcoal-900 dark:border-charcoal-700">
                  <img
                    src={qrCodeApiUrl}
                    alt="QR Code Téléchargement Mobile"
                    className="size-32 rounded-lg"
                    loading="lazy"
                  />
                </div>
                <div className="flex-1 space-y-3 text-center sm:text-left">
                  <div>
                    <div className="flex items-center justify-center sm:justify-start gap-2">
                      <h4 className="font-bold text-sm text-charcoal-950 dark:text-slate-100">
                        Application Mobile Android (v{MOBILE_VERSION})
                      </h4>
                      <span className="rounded bg-lime-500/15 px-2 py-0.5 text-[10px] font-semibold text-lime-700 dark:text-lime-300">
                        APK direct
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Scannez le QR Code avec votre téléphone pour télécharger directement l'APK de
                      l'application caisse & mobile.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                    <a
                      href={apkUrl}
                      download
                      className="inline-flex items-center gap-1.5 rounded-lg bg-lime-500 px-3 py-2 text-xs font-bold text-charcoal-950 hover:bg-lime-400 transition-colors"
                    >
                      <Download className="size-3.5" />
                      <span>Télécharger APK ({MOBILE_VERSION})</span>
                    </a>
                    <button
                      type="button"
                      onClick={() => handleCopyLink(apkUrl)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-charcoal-700 dark:bg-charcoal-800 dark:text-slate-200 dark:hover:bg-charcoal-700 transition-colors"
                    >
                      {copiedLink ? (
                        <>
                          <Check className="size-3.5 text-lime-600 dark:text-lime-400" />
                          <span>Lien copié !</span>
                        </>
                      ) : (
                        <>
                          <Copy className="size-3.5" />
                          <span>Copier le lien APK</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Onglet PWA */}
            {activeTab === 'pwa' && (
              <div className="rounded-xl border border-slate-200 p-4 dark:border-charcoal-700 bg-slate-50/50 dark:bg-charcoal-800/40 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="grid size-10 place-items-center rounded-lg bg-sky-500/10 text-sky-600 dark:bg-sky-500/20 dark:text-sky-400">
                    <Globe className="size-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-charcoal-950 dark:text-slate-100">
                      Application Web Progressive (PWA)
                    </h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Installez Velko directement depuis votre navigateur sans téléchargement de
                      fichier exécutable.
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-charcoal-700">
                  {isPwaInstalled ? (
                    <div className="flex items-center gap-2 text-xs text-lime-600 dark:text-lime-400 font-medium">
                      <Check className="size-4" />
                      <span>L'application PWA est déjà installée sur cet appareil.</span>
                    </div>
                  ) : pwaPrompt ? (
                    <Button
                      onClick={handleInstallPwa}
                      className="bg-sky-600 hover:bg-sky-500 text-white text-xs gap-2"
                    >
                      <Download className="size-3.5" />
                      <span>Installer l'application PWA</span>
                    </Button>
                  ) : (
                    <p className="text-xs text-slate-500 dark:text-slate-400 italic">
                      Pour installer en PWA : Cliquez sur l'icône "Installer" dans la barre
                      d'adresse de votre navigateur Chrome / Edge / Safari.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer de réassurance et lien GitHub Releases */}
          <div className="flex items-center justify-between border-t border-slate-100 pt-4 dark:border-charcoal-800 text-xs text-slate-500 dark:text-slate-400">
            <div className="flex items-center gap-1.5">
              <ShieldCheck className="size-4 text-lime-600 dark:text-lime-400" />
              <span>Logiciel certifié, open-source et sécurisé</span>
            </div>
            <a
              href={GITHUB_RELEASES_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-medium text-slate-600 hover:text-charcoal-950 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
            >
              <span>GitHub Releases</span>
              <ExternalLink className="size-3" />
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
